import type { ReactNode } from 'react';
import { TaskLanguageSelect, TaskNotice, TaskSetup } from '@/features/contribution-kit';
import type { SpeakLanguageOption } from '../types';
import type { SpeakContributeCopy } from '../speak-contribute-copy';
import { isRecordingDeviceSelectionValid, RecordingDeviceFields, type RecordingDeviceSelection } from '@/features/recording-device-metadata';

type SpeakSetupProps = {
  t: {
    speakContributeTitle: string;
    speakContributeSubtitle: string;
    phrasesToRead: string;
    minutesRequired: string;
    speakInstruction1: string;
    speakInstruction2: string;
    speakInstruction3: string;
    speakInstruction4: string;
    loading: string;
    startSession: string;
    selectLanguage: string;
    chooseLanguageToRecord: string;
  };
  selectedLanguage: string;
  recordingDevice: RecordingDeviceSelection;
  activeLanguages: SpeakLanguageOption[];
  isLoadingLanguages: boolean;
  isStartingSession: boolean;
  sessionResumeActive: boolean;
  onLanguageChange: (language: string) => void;
  onRecordingDeviceChange: (device: RecordingDeviceSelection) => void;
  copy: SpeakContributeCopy;
  onBack: () => void;
  onStart: () => void;
  /** Where the session is collected (spec 004); required before starting. */
  placeStep: ReactNode;
  isPlaceReady: boolean;
};

/** Before an "Enregistrer" session: collection place, language, rules, start. */
export function SpeakSetup({
  t,
  copy,
  selectedLanguage,
  recordingDevice,
  activeLanguages,
  isLoadingLanguages,
  isStartingSession,
  sessionResumeActive,
  onLanguageChange,
  onRecordingDeviceChange,
  onBack,
  onStart,
  placeStep,
  isPlaceReady,
}: SpeakSetupProps) {
  return (
    <TaskSetup
      task="speak"
      title={t.speakContributeTitle}
      description={t.speakContributeSubtitle}
      stats={[
        { value: '5', label: t.phrasesToRead },
        { value: '~5', label: t.minutesRequired },
      ]}
      instructions={[t.speakInstruction1, t.speakInstruction2, t.speakInstruction3, t.speakInstruction4]}
      notices={
        sessionResumeActive && selectedLanguage ? (
          <TaskNotice tone="info" title={copy.resumeTitle}>
            {copy.resumeBody}
          </TaskNotice>
        ) : null
      }
      startLabel={isStartingSession ? t.loading || 'Chargement...' : t.startSession}
      onStart={onStart}
      startDisabled={!selectedLanguage || !isPlaceReady || !isRecordingDeviceSelectionValid(recordingDevice)}
      starting={isStartingSession}
      onBack={onBack}
    >
      {placeStep}
      <RecordingDeviceFields copy={copy.recordingDevice} value={recordingDevice} onChange={onRecordingDeviceChange} idPrefix="speak-recording-device" />
      <div className="space-y-2">
        <label htmlFor="speak-language" className="text-sm font-medium text-foreground">
          {t.selectLanguage}
        </label>
        <TaskLanguageSelect
          id="speak-language"
          value={selectedLanguage}
          onChange={onLanguageChange}
          languages={activeLanguages}
          isLoading={isLoadingLanguages}
          placeholder={t.chooseLanguageToRecord}
        />
      </div>
    </TaskSetup>
  );
}
